<template>
  <div class="container mt-3">
    <div class="row">
      <div class="col s12">
        <div class="card">
          <div class="card-title primary white-text">
            <i class="material-icons icon">local_shipping</i>
            Shipments
          </div>

          <div class="card-content">
            <div class="mb-3">
              Your list of shipments are shown below
              <div class="right">
                <div class="input-field">
                  <select v-model="shipmentFilter">
                    <option value="">Show All</option>
                    <option value="SHIPPED">Dispatched</option>
                    <option value="PENDING">Scheduled</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="clearfix"></div>

            <div id="data-table">
              <div class="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th
                        v-for="header in headers"
                        :key="header.text"
                        :class="[
                          header.align === 'right' ? 'right-align' : (header.align === 'center' ? 'center-align' : 'left-align')
                        ]"
                        :width="header.width"
                      >
                        {{ header.text }}
                      </th>
                    </tr>
                  </thead>

                  <tbody v-if="loadingList || errorList !== ''">
                    <tr>
                      <td colspan="4">
                        <div v-if="loadingList" class="progress">
                          <div class="indeterminate"></div>
                        </div>
                        <div v-if="errorList !== ''" class="center-align">
                          {{ errorList }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tbody class="table-body" v-else>
                    <tr
                      v-for="item in shipmentList"
                      :key="item.id"
                    >
                      <td>
                        {{ formatDate(item.shipDate) }}
                      </td>
                      <td>
                        <div>
                          <b>{{ item.deliveryChildName }}</b><br>
                          C/o {{ item.deliveryName }}
                        </div>
                        <div v-html="item.deliveryAddress" class="small-text"></div>
                        <div class="small-text">
                          <b>Ph:</b> {{ item.deliveryPhone }}
                        </div>
                      </td>
                      <td>
                        <div>
                          {{ item.categoryTitle }} ({{ item.planTitle }})
                        </div>
                      </td>
                      <td class="center-align">
                        <span
                          class="new badge grey"
                          data-badge-caption=""
                          v-if="item.shipmentStatus === '0'"
                        >SCHEDULED</span>
                        <span
                          class="new badge green"
                          data-badge-caption=""
                          v-if="item.shipmentStatus === '1'"
                        >DISPATCHED</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="totalPages > 1" class="center-align mt-4">
              <ul class="pagination">
                <li :class="pagination.page === 1 ? 'disabled' : 'waves-effect'">
                  <a @click="(pagination.page > 1) ? changePage(pagination.page - 1) : null"><i class="material-icons">chevron_left</i></a>
                </li>
                <li v-for="n in totalPages" :key="n" :class="pagination.page === n ? 'active' : 'waves-effect'">
                  <a @click="changePage(n)">{{ n }}</a>
                </li>
                <li :class="pagination.page === totalPages ? 'disabled' : 'waves-effect'">
                  <a @click="(pagination.page < totalPages) ? changePage(pagination.page + 1) : null"><i class="material-icons">chevron_right</i></a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import the API functions
import httpApi from '@/utils/api'

// Date formatting helper
import format from 'date-fns/format'

export default {
  data () {
    return {
      // Variable to save shipment list
      shipmentList: [],

      // Total shipment count, required for pagination
      totalShipments: 0,

      // Loading status for list of items
      loadingList: true,

      // Error message when loading error or no items returned
      errorList: '',

      // Set table defaults
      pagination: {
        page: 1
      },

      shipmentFilter: '',

      // Table headers
      headers: [
        { text: 'Ship Date', align: 'left', value: 'shipDate', sortable: false },
        { text: 'Delivery', align: 'left', value: 'deliveryChildName', sortable: false },
        { text: 'Subscription', align: 'left', value: 'categoryTitle', sortable: false },
        { text: 'Status', align: 'center', value: 'shipmentStatus', width: '10%', sortable: false }
      ]
    }
  },

  watch: {
    shipmentFilter (val) {
      // Get data from API and populate table
      this.getFreshData()
    }
  },

  mounted () {
    var elems = document.querySelectorAll('select')
    // eslint-disable-next-line
    var instances = M.FormSelect.init(elems)

    // On load, get data from API and populate table
    this.getFreshData()
  },

  computed: {
    // Total number of pages
    totalPages () {
      return Math.ceil(this.totalShipments / 10)
    }
  },

  methods: {
    getFreshData () {
      this.getShipmentListFromApi()
        .then(data => {
          this.assignFetchedList(data)
        })
        .catch((err) => {
          this.errorList = err.message
        })
    },

    formatDate (val) {
      return format(val, 'DD MMM YYYY')
    },

    // Change the page
    changePage (pageNo) {
      document.getElementById('data-table').style.height = '125px'
      this.pagination.page = pageNo
      this.getFreshData()
    },

    // Wrapper for the API call
    getShipmentListFromApi () {
      // Activate loading animation
      this.loadingList = true

      // Promise a return
      return new Promise((resolve, reject) => {
        // Which page to get
        const page = this.pagination.page

        let params = '?'
        params += 'pageNumber=' + page
        params += '&filterStatus=' + this.shipmentFilter

        // Make the API call
        httpApi.callWithToken({
          url: 'subscription-shipment' + params,
          method: 'get'
        })
          .then((resp) => {
            // Deactivate the loading animation
            this.loadingList = false

            // Resolve the promise
            resolve({
              // List of items
              items: resp.data.items,

              // Total number of items returned
              total: resp.data.total
            })
          })
          // If an error occurs
          .catch((err) => {
            // Deactivate the loading animation
            this.loadingList = false

            // Broken promise
            reject(err)
          })
      })
    },

    // API data is assigned to multiple variables
    assignFetchedList (data) {
      this.totalShipments = parseInt(data.total)
      this.shipmentList = data.items

      this.errorList = (data.total === 0) ? 'No items found' : ''

      // Animate the height after new data loads
      httpApi.animateTableHeight()
    }
  }
}
</script>
